import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  RefreshCw,
  Check,
  X,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  Upload,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Clock,
  Edit3,
  Save,
  Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPricingCalendar() {
  const { token } = useAuth();
  
  // State
  const [units, setUnits] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [priceData, setPriceData] = useState({});
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [mobileEditMode, setMobileEditMode] = useState(false);
  const [mobilePrices, setMobilePrices] = useState({});
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  
  // Selection
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  
  // Dialog
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [quickApplyDialogOpen, setQuickApplyDialogOpen] = useState(false);
  const [applyAllAiDialogOpen, setApplyAllAiDialogOpen] = useState(false);
  const [applyingAllAi, setApplyingAllAi] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [applyToUnits, setApplyToUnits] = useState({});
  const [quickDateFrom, setQuickDateFrom] = useState('');
  const [quickDateTo, setQuickDateTo] = useState('');
  const [quickPrice, setQuickPrice] = useState('');
  
  // Inline edit
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Sync status
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncErrors, setSyncErrors] = useState([]);
  const [syncErrorsDialogOpen, setSyncErrorsDialogOpen] = useState(false);
  const [retryingSync, setRetryingSync] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [syncBreakdown, setSyncBreakdown] = useState({ pending: 0, error: 0, local_only: 0 });
  
  // Competitor Panel
  const [competitorPanelOpen, setCompetitorPanelOpen] = useState(false);
  const [competitorData, setCompetitorData] = useState(null);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [priceAlert, setPriceAlert] = useState(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      // Usa formato locale per evitare problemi di timezone
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      const [unitsRes, bookingsRes, calendarPricesRes] = await Promise.all([
        axios.get(`${API}/admin/units`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings?filter=all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/calendar-prices?date_from=${startDate}&date_to=${endDate}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUnits(unitsRes.data.filter(u => u.attivo));
      setBookings(bookingsRes.data);
      
      const unitSelection = {};
      unitsRes.data.filter(u => u.attivo).forEach(u => {
        unitSelection[u.id] = true;
      });
      setApplyToUnits(unitSelection);
      
      const pricesFromServer = calendarPricesRes.data.prices || {};
      
      const prices = {};
      unitsRes.data.filter(u => u.attivo).forEach(unit => {
        prices[unit.id] = {};
        const unitPrices = pricesFromServer[unit.id] || {};
        
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        for (let day = 1; day <= daysInMonth; day++) {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const serverData = unitPrices[dateStr] || {};
          
          const booking = bookingsRes.data.find(b => {
            if (b.unit_id !== unit.id || b.status === 'cancelled') return false;
            const arrival = new Date(b.data_arrivo);
            const departure = new Date(b.data_partenza);
            const current = new Date(dateStr);
            return current >= arrival && current < departure;
          });
          
          prices[unit.id][dateStr] = {
            price: serverData.price || unit.prezzo_base || 100,
            source: serverData.source || 'base',
            periodName: serverData.period_name,
            booking: booking || null,
            syncStatus: serverData.sync_status || 'unknown'
          };
        }
      });
      
      setPriceData(prices);
      
      // Carica contatore prezzi da sincronizzare
      try {
        const pendingRes = await axios.get(`${API}/admin/pricing/pending-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingSyncCount(pendingRes.data.pending_count || 0);
        setSyncBreakdown(pendingRes.data.breakdown || { pending: 0, error: 0, local_only: 0 });
      } catch (e) {
        console.log('Pending count not available');
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [token, currentMonth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get AI suggestions
  const getAISuggestions = async () => {
    setAiLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      const suggestions = {};
      const weeks = [];
      let weekStart = new Date(year, month, 1);
      while (weekStart.getMonth() === month) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        if (weekEnd.getMonth() !== month) {
          weekEnd.setDate(0);
          weekEnd.setMonth(month + 1);
        }
        weeks.push({
          start: weekStart.toISOString().split('T')[0],
          end: weekEnd.toISOString().split('T')[0]
        });
        weekStart = new Date(weekEnd);
        weekStart.setDate(weekStart.getDate() + 1);
      }
      
      toast.info(`Analisi AI in corso per ${weeks.length} settimane...`);
      
      for (let i = 0; i < weeks.length; i++) {
        const week = weeks[i];
        try {
          const response = await axios.post(
            `${API}/admin/pricing/search-competitors`,
            {},
            { 
              headers: { Authorization: `Bearer ${token}` },
              params: { checkin: week.start, checkout: week.end }
            }
          );
          
          if (response.data.success && response.data.analysis?.recommended_price) {
            const suggested = response.data.analysis.recommended_price.suggested;
            const reason = response.data.analysis.recommended_price.reasoning;
            
            let d = new Date(week.start);
            const endD = new Date(week.end);
            while (d <= endD) {
              const dateStr = d.toISOString().split('T')[0];
              suggestions[dateStr] = {
                suggested,
                reason,
                min: response.data.analysis.recommended_price.min,
                max: response.data.analysis.recommended_price.max
              };
              d.setDate(d.getDate() + 1);
            }
          }
        } catch (err) {
          console.error('Error getting suggestion for week:', week, err);
        }
      }
      
      // Log dettagliato dei risultati
      const totalDays = Object.keys(suggestions).length;
      const uniquePrices = [...new Set(Object.values(suggestions).map(s => s.suggested))];
      
      console.log(`[AI Pricing] Days processed: ${totalDays}`);
      console.log(`[AI Pricing] Unique suggested prices: ${uniquePrices.join(', ')}`);
      
      setAiSuggestions(suggestions);
      
      if (totalDays > 0) {
        toast.success(`${totalDays} suggerimenti AI caricati! Clicca su una data per applicare.`, {
          duration: 5000,
          description: `Range: €${Math.min(...uniquePrices)} - €${Math.max(...uniquePrices)}`
        });
      } else {
        toast.warning('0 suggerimenti AI generati - l\'analisi non ha restituito prezzi', {
          duration: 5000
        });
      }
      
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
      toast.error('Errore caricamento suggerimenti AI');
    } finally {
      setAiLoading(false);
    }
  };

  // Apply ALL AI suggestions at once
  const applyAllAiSuggestions = async () => {
    const suggestionDates = Object.keys(aiSuggestions);
    if (suggestionDates.length === 0) {
      toast.error('Nessun suggerimento AI da applicare. Clicca prima "Suggerisci Prezzi AI"');
      return;
    }
    
    setApplyingAllAi(true);
    
    // Group consecutive dates with same price to minimize API calls
    const priceGroups = {};
    suggestionDates.sort().forEach(dateStr => {
      const price = aiSuggestions[dateStr].suggested;
      if (!priceGroups[price]) {
        priceGroups[price] = [];
      }
      priceGroups[price].push(dateStr);
    });
    
    let appliedCount = 0;
    let errorCount = 0;
    let modifiedCount = 0;
    const results = [];
    
    // Get selected units
    const selectedUnitIds = Object.entries(applyToUnits)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    if (selectedUnitIds.length === 0) {
      toast.error('Seleziona almeno una unità');
      setApplyingAllAi(false);
      return;
    }
    
    toast.info(`Applicazione ${suggestionDates.length} suggerimenti AI a ${selectedUnitIds.length} unità...`);
    
    try {
      for (const unitId of selectedUnitIds) {
        for (const [price, dates] of Object.entries(priceGroups)) {
          // Find consecutive date ranges
          const ranges = [];
          let rangeStart = dates[0];
          let rangeEnd = dates[0];
          
          for (let i = 1; i < dates.length; i++) {
            const prevDate = new Date(dates[i - 1]);
            const currDate = new Date(dates[i]);
            const diffDays = (currDate - prevDate) / (1000 * 60 * 60 * 24);
            
            if (diffDays === 1) {
              rangeEnd = dates[i];
            } else {
              ranges.push({ start: rangeStart, end: rangeEnd });
              rangeStart = dates[i];
              rangeEnd = dates[i];
            }
          }
          ranges.push({ start: rangeStart, end: rangeEnd });
          
          // Apply each range
          for (const range of ranges) {
            try {
              // Check if price is different from current
              const currentPrice = priceData[unitId]?.[range.start]?.price;
              if (currentPrice && Math.abs(currentPrice - parseFloat(price)) < 1) {
                console.log(`[AI Apply] Skipping ${range.start}: price unchanged (€${currentPrice})`);
                continue;
              }
              
              const response = await axios.post(
                `${API}/admin/pricing/apply-suggestion`,
                {},
                { 
                  headers: { Authorization: `Bearer ${token}` },
                  params: { 
                    date_from: range.start,
                    date_to: range.end,
                    price: parseFloat(price),
                    unit_id: unitId,
                    sync_beds24: true
                  }
                }
              );
              
              if (response.data.success) {
                appliedCount++;
                // Count days in range
                const startD = new Date(range.start);
                const endD = new Date(range.end);
                const days = Math.ceil((endD - startD) / (1000 * 60 * 60 * 24)) + 1;
                modifiedCount += days;
                
                results.push({
                  unit: units.find(u => u.id === unitId)?.nome,
                  range: `${range.start} → ${range.end}`,
                  price: price,
                  status: response.data.verified_beds24 ? 'verified' : 'applied',
                  days
                });
              }
            } catch (err) {
              errorCount++;
              console.error(`Error applying AI suggestion for ${range.start}-${range.end}:`, err);
            }
          }
        }
      }
      
      // Final summary
      const summary = `${modifiedCount} prezzi modificati (${appliedCount} periodi applicati, ${errorCount} errori)`;
      console.log(`[AI Apply] ${summary}`);
      
      if (modifiedCount > 0) {
        toast.success(summary, { duration: 5000 });
        setApplyAllAiDialogOpen(false);
        setAiSuggestions({}); // Clear suggestions after applying
        loadData(); // Refresh calendar
      } else if (errorCount > 0) {
        toast.error(`0 prezzi modificati - ${errorCount} errori durante l'applicazione`);
      } else {
        toast.warning('0 prezzi modificati - i prezzi AI sono uguali a quelli attuali', { duration: 5000 });
      }
      
    } catch (error) {
      console.error('Error applying all AI suggestions:', error);
      toast.error('Errore applicazione suggerimenti AI');
    } finally {
      setApplyingAllAi(false);
    }
  };

  // Sync all to Beds24 with verification
  const syncAllToBeds24 = async () => {
    setSyncLoading(true);
    setSyncStatus({ status: 'syncing', message: 'Sincronizzazione prezzi modificati...' });
    try {
      // Usa il nuovo endpoint che sincronizza SOLO le date modificate (pending)
      const response = await axios.post(
        `${API}/admin/pricing/sync-pending-to-beds24`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSyncStatus({
          status: 'verified',
          message: response.data.message,
          synced_count: response.data.synced,
          error_count: response.data.errors
        });
        
        if (response.data.synced > 0) {
          toast.success(`✓ ${response.data.synced} prezzi sincronizzati con Beds24`);
        } else {
          toast.info('Nessun prezzo da sincronizzare');
        }
        
        if (response.data.errors > 0) {
          toast.error(`✗ ${response.data.errors} errori di sincronizzazione`);
        }
        
        setSyncDialogOpen(false);
        loadData();
      } else {
        setSyncStatus({
          status: 'error',
          message: response.data.message
        });
        toast.error(response.data.message);
      }
    } catch (error) {
      setSyncStatus({
        status: 'error',
        message: error.response?.data?.detail || 'Errore sincronizzazione Beds24'
      });
      toast.error(error.response?.data?.detail || 'Errore sincronizzazione Beds24');
    } finally {
      setSyncLoading(false);
    }
  };

  // Load sync errors
  const loadSyncErrors = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing/sync-errors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncErrors(response.data.errors || []);
      return response.data;
    } catch (error) {
      console.error('Error loading sync errors:', error);
      return { errors: [], count: 0 };
    }
  };

  // Retry all failed syncs
  const retryAllFailed = async () => {
    setRetryingSync(true);
    try {
      const response = await axios.post(
        `${API}/admin/pricing/retry-all-failed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.retry_success > 0) {
        toast.success(`✓ ${response.data.retry_success} prezzi sincronizzati con successo!`);
      }
      if (response.data.retry_failed > 0) {
        toast.error(`✗ ${response.data.retry_failed} prezzi ancora in errore`);
      }
      
      // Ricarica errori e dati
      await loadSyncErrors();
      loadData();
      
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore retry sincronizzazione');
      return null;
    } finally {
      setRetryingSync(false);
    }
  };

  // Load competitor data and check for price alerts
  const loadCompetitorData = async () => {
    setLoadingCompetitors(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      // Prossimo weekend nel mese corrente
      const checkin = `${year}-${String(month + 1).padStart(2, '0')}-15`;
      const checkout = `${year}-${String(month + 1).padStart(2, '0')}-17`;
      
      const response = await axios.get(
        `${API}/admin/ai/competitive-prices`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { checkin, checkout, use_real_scraping: true }
        }
      );
      
      setCompetitorData(response.data);
      
      // Check for price alerts (prezzi sotto media)
      const suggestions = response.data.suggestions || [];
      const belowAverage = suggestions.filter(s => s.comparison === 'sotto_media');
      
      if (belowAverage.length > 0) {
        setPriceAlert({
          type: 'warning',
          title: 'Prezzi Sotto la Media!',
          message: `${belowAverage.length} unità hanno prezzi sotto la media di mercato (€${response.data.market_data?.avg_price || 0})`,
          units: belowAverage
        });
      } else {
        setPriceAlert(null);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error loading competitor data:', error);
      toast.error('Errore caricamento dati competitor');
      return null;
    } finally {
      setLoadingCompetitors(false);
    }
  };

  // Save price for a cell
  const savePriceForCell = async (unitId, dateStr, price) => {
    try {
      const response = await axios.post(
        `${API}/admin/pricing/apply-suggestion`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            date_from: dateStr,
            date_to: dateStr,
            price: parseFloat(price),
            unit_id: unitId,
            sync_beds24: true
          }
        }
      );
      
      if (response.data.verified_beds24) {
        toast.success(`€${price} sincronizzato e verificato su Beds24!`);
      } else if (response.data.synced_beds24) {
        toast.success(`€${price} sincronizzato su Beds24`);
      } else {
        toast.success(`€${price} salvato localmente`);
      }
      
      return response.data;
    } catch (error) {
      toast.error('Errore salvataggio');
      throw error;
    }
  };

  // Inline edit - save price
  const saveInlinePrice = async () => {
    if (!editingCell || !editValue) {
      setEditingCell(null);
      return;
    }
    
    const price = parseFloat(editValue);
    if (isNaN(price) || price <= 0) {
      toast.error('Prezzo non valido');
      return;
    }
    
    try {
      await savePriceForCell(editingCell.unitId, editingCell.dateStr, price);
      setEditingCell(null);
      setEditValue('');
      loadData();
    } catch (error) {
      // Error already shown by savePriceForCell
    }
  };

  // Handle double click to edit
  const handleCellDoubleClick = (unitId, dateStr, currentPrice, hasBooking) => {
    if (hasBooking) return;
    setEditingCell({ unitId, dateStr });
    setEditValue(String(currentPrice || ''));
  };

  // Calendar navigation
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setAiSuggestions({});
    setSelectedDate(null);
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setAiSuggestions({});
    setSelectedDate(null);
  };

  // Desktop date selection
  const handleDateMouseDown = (dateStr) => {
    if (isMobile) return;
    setIsSelecting(true);
    setSelectionStart(dateStr);
    setSelectedDates([dateStr]);
  };
  
  const handleDateMouseEnter = (dateStr) => {
    if (isMobile) return;
    if (isSelecting && selectionStart) {
      const start = new Date(selectionStart);
      const current = new Date(dateStr);
      const dates = [];
      
      const [first, last] = start < current ? [start, current] : [current, start];
      for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }
      setSelectedDates(dates);
    }
  };
  
  const handleDateMouseUp = () => {
    if (isMobile) return;
    setIsSelecting(false);
    if (selectedDates.length > 0) {
      const firstDate = selectedDates[0];
      if (aiSuggestions[firstDate]) {
        setNewPrice(aiSuggestions[firstDate].suggested.toString());
      }
      setApplyDialogOpen(true);
    }
  };

  // Mobile: select date
  const handleMobileDateSelect = (dateStr) => {
    if (dateRangeStart && !dateRangeEnd) {
      // Selecting end of range
      const start = new Date(dateRangeStart);
      const end = new Date(dateStr);
      if (end >= start) {
        setDateRangeEnd(dateStr);
        setSelectedDate(dateStr);
      } else {
        setDateRangeStart(dateStr);
        setDateRangeEnd(null);
        setSelectedDate(dateStr);
      }
    } else {
      // Start new selection
      setDateRangeStart(dateStr);
      setDateRangeEnd(null);
      setSelectedDate(dateStr);
    }
  };

  // Mobile: save all edited prices
  const saveMobilePrices = async () => {
    const entries = Object.entries(mobilePrices);
    if (entries.length === 0) {
      toast.error('Nessun prezzo modificato');
      return;
    }
    
    setSyncLoading(true);
    let successCount = 0;
    
    for (const [key, price] of entries) {
      const [unitId, dateStr] = key.split('_');
      try {
        await savePriceForCell(unitId, dateStr, price);
        successCount++;
      } catch (error) {
        console.error('Error saving price:', error);
      }
    }
    
    toast.success(`${successCount} prezzi salvati!`);
    setMobilePrices({});
    setMobileEditMode(false);
    setSyncLoading(false);
    loadData();
  };

  // Apply price (desktop)
  const applyPrice = async () => {
    if (!newPrice || selectedDates.length === 0) {
      toast.error('Seleziona date e inserisci prezzo');
      return;
    }
    
    const selectedUnitIds = Object.entries(applyToUnits)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    if (selectedUnitIds.length === 0) {
      toast.error('Seleziona almeno una unità');
      return;
    }
    
    try {
      const dateFrom = selectedDates.sort()[0];
      const dateTo = selectedDates.sort()[selectedDates.length - 1];
      
      for (const unitId of selectedUnitIds) {
        await axios.post(
          `${API}/admin/pricing/apply-suggestion`,
          {},
          { 
            headers: { Authorization: `Bearer ${token}` },
            params: { 
              date_from: dateFrom,
              date_to: dateTo,
              price: parseFloat(newPrice),
              unit_id: unitId,
              sync_beds24: true
            }
          }
        );
      }
      
      toast.success(`Prezzo €${newPrice}/notte applicato a ${selectedUnitIds.length} unità!`);
      setApplyDialogOpen(false);
      setSelectedDates([]);
      setNewPrice('');
      loadData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore applicazione prezzo');
    }
  };

  // Quick apply price
  const quickApplyPrice = async () => {
    if (!quickDateFrom || !quickDateTo || !quickPrice) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    const selectedUnitIds = Object.entries(applyToUnits)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    if (selectedUnitIds.length === 0) {
      toast.error('Seleziona almeno una unità');
      return;
    }
    
    try {
      for (const unitId of selectedUnitIds) {
        await axios.post(
          `${API}/admin/pricing/apply-suggestion`,
          {},
          { 
            headers: { Authorization: `Bearer ${token}` },
            params: { 
              date_from: quickDateFrom,
              date_to: quickDateTo,
              price: parseFloat(quickPrice),
              unit_id: unitId,
              sync_beds24: true
            }
          }
        );
      }
      
      toast.success(`Prezzo €${quickPrice}/notte applicato dal ${quickDateFrom} al ${quickDateTo}!`);
      setQuickApplyDialogOpen(false);
      setQuickDateFrom('');
      setQuickDateTo('');
      setQuickPrice('');
      loadData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore applicazione prezzo');
    }
  };

  // Render calendar days
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({
        day: i,
        dateStr,
        dayName: dayNames[new Date(year, month, i).getDay()],
        isWeekend: [0, 6].includes(new Date(year, month, i).getDay()),
        isToday: dateStr === new Date().toISOString().split('T')[0]
      });
    }
    
    return days;
  };

  const days = renderCalendar();
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

  const getPriceComparison = (currentPrice, suggestedPrice) => {
    if (!suggestedPrice) return null;
    const diff = suggestedPrice - currentPrice;
    const percent = ((diff / currentPrice) * 100).toFixed(0);
    
    if (diff > 5) return { icon: TrendingUp, color: 'text-green-600', label: `+${percent}%` };
    if (diff < -5) return { icon: TrendingDown, color: 'text-red-600', label: `${percent}%` };
    return { icon: Minus, color: 'text-gray-400', label: '~' };
  };

  // Get selected date range for mobile
  const getSelectedDateRange = () => {
    if (dateRangeStart && dateRangeEnd) {
      return `${dateRangeStart} → ${dateRangeEnd}`;
    } else if (dateRangeStart) {
      return dateRangeStart;
    }
    return null;
  };

  // Check if date is in selected range
  const isDateInRange = (dateStr) => {
    if (!dateRangeStart) return false;
    if (!dateRangeEnd) return dateStr === dateRangeStart;
    return dateStr >= dateRangeStart && dateStr <= dateRangeEnd;
  };

  // ============== MOBILE VIEW ==============
  const renderMobileView = () => {
    const selectedDateData = selectedDate ? days.find(d => d.dateStr === selectedDate) : null;
    
    return (
      <div className="space-y-4 pb-24">
        {/* Date selector */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <Button variant="ghost" size="sm" onClick={prevMonth} className="min-h-[44px] min-w-[44px]">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle className="text-lg">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={nextMonth} className="min-h-[44px] min-w-[44px]">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="px-2">
            {/* Date range indicator */}
            {getSelectedDateRange() && (
              <div className="mb-3 p-2 bg-blue-50 rounded-lg text-sm text-blue-800 flex justify-between items-center">
                <span>Selezionato: {getSelectedDateRange()}</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setDateRangeStart(null); setDateRangeEnd(null); setSelectedDate(null); }}
                  className="min-h-[44px]"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* Compact date grid */}
            <div className="grid grid-cols-7 gap-1">
              {['D', 'L', 'M', 'M', 'G', 'V', 'S'].map((d, i) => (
                <div key={i} className="text-center text-xs text-gray-500 py-1">{d}</div>
              ))}
              
              {/* Empty cells for first day offset */}
              {Array.from({ length: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              
              {days.map(day => {
                const hasBooking = units.some(u => priceData[u.id]?.[day.dateStr]?.booking);
                const isSelected = isDateInRange(day.dateStr);
                const hasSuggestion = aiSuggestions[day.dateStr];
                const hasSyncPending = units.some(u => {
                  const status = priceData[u.id]?.[day.dateStr]?.syncStatus;
                  return status === 'pending' || status === 'error' || status === 'local_only';
                });
                
                return (
                  <button
                    key={day.dateStr}
                    onClick={() => handleMobileDateSelect(day.dateStr)}
                    className={`
                      min-h-[44px] min-w-[44px] p-1 rounded-lg text-center transition-all relative
                      ${day.isToday ? 'ring-2 ring-[#C5A059]' : ''}
                      ${isSelected ? 'bg-blue-500 text-white' : ''}
                      ${hasBooking && !isSelected ? 'bg-green-100' : ''}
                      ${hasSuggestion && !isSelected && !hasBooking ? 'bg-purple-50' : ''}
                      ${!isSelected && !hasBooking && !hasSuggestion ? 'hover:bg-gray-100' : ''}
                    `}
                    data-testid={`mobile-date-${day.dateStr}`}
                  >
                    <div className="text-sm font-medium">{day.day}</div>
                    {/* Indicators row */}
                    <div className="flex justify-center gap-0.5 mt-0.5">
                      {hasSuggestion && !isSelected && (
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                      )}
                      {hasSyncPending && !isSelected && (
                        <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Compact legend */}
            <div className="mt-3 flex flex-wrap gap-2 text-xs justify-center">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-100 rounded border border-green-300" />
                Prenotato
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                AI
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                Da sync
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded ring-2 ring-[#C5A059]" />
                Oggi
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Unit cards for selected date */}
        {selectedDate && (
          <div className="space-y-3">
            <div className="flex justify-between items-center px-1">
              <h3 className="font-semibold text-gray-700">
                {selectedDateData?.dayName} {selectedDateData?.day} {monthNames[currentMonth.getMonth()]}
              </h3>
              {!mobileEditMode ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setMobileEditMode(true)}
                  className="min-h-[44px]"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Modifica
                </Button>
              ) : (
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => setMobileEditMode(false)}
                  className="min-h-[44px]"
                >
                  <X className="w-4 h-4 mr-2" />
                  Annulla
                </Button>
              )}
            </div>
            
            {/* AI Suggestion banner */}
            {aiSuggestions[selectedDate] && (
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">Suggerimento AI</span>
                    </div>
                    <span className="text-xl font-bold text-purple-700">€{aiSuggestions[selectedDate].suggested}</span>
                  </div>
                  <p className="text-xs text-purple-600 mt-1">{aiSuggestions[selectedDate].reason}</p>
                </CardContent>
              </Card>
            )}
            
            {/* Unit cards */}
            {units.map(unit => {
              const data = priceData[unit.id]?.[selectedDate] || {};
              const suggestion = aiSuggestions[selectedDate];
              const editKey = `${unit.id}_${selectedDate}`;
              const editedPrice = mobilePrices[editKey];
              const displayPrice = editedPrice !== undefined ? editedPrice : data.price;
              const syncStatus = data.syncStatus;
              
              return (
                <Card key={unit.id} className={`${data.booking ? 'border-green-300 bg-green-50' : ''} ${syncStatus === 'error' ? 'border-red-300' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-800">{unit.nome}</h4>
                          {/* Sync status indicator */}
                          {syncStatus === 'verified' && (
                            <CheckCircle2 className="w-4 h-4 text-green-500" title="Sincronizzato ✓" />
                          )}
                          {syncStatus === 'synced' && (
                            <Check className="w-4 h-4 text-blue-500" title="Sync OK" />
                          )}
                          {syncStatus === 'pending' && (
                            <Clock className="w-4 h-4 text-yellow-500" title="Da sincronizzare" />
                          )}
                          {syncStatus === 'error' && (
                            <AlertCircle className="w-4 h-4 text-red-500" title="Errore sync" />
                          )}
                        </div>
                        {data.booking && (
                          <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            Prenotato: {data.booking.nome_ospite?.split(' ')[0]}
                          </span>
                        )}
                        {data.source && !data.booking && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            data.source === 'manual' ? 'bg-blue-100 text-blue-700' :
                            data.source === 'ai_suggestion' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {data.source === 'manual' ? 'Manuale' : data.source === 'ai_suggestion' ? 'AI' : data.source}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-800">€{displayPrice || '-'}</div>
                        {suggestion && !data.booking && (
                          <div className={`text-xs flex items-center justify-end gap-1 ${suggestion.suggested > data.price ? 'text-green-600' : suggestion.suggested < data.price ? 'text-orange-600' : 'text-gray-500'}`}>
                            {suggestion.suggested > data.price && <ArrowUp className="w-3 h-3" />}
                            {suggestion.suggested < data.price && <ArrowDown className="w-3 h-3" />}
                            AI: €{suggestion.suggested}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {mobileEditMode && !data.booking && (
                      <div className="mt-3 pt-3 border-t">
                        <Label className="text-sm text-gray-600">Nuovo prezzo</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editedPrice !== undefined ? editedPrice : ''}
                            onChange={(e) => setMobilePrices({...mobilePrices, [editKey]: e.target.value})}
                            placeholder={String(data.price || 100)}
                            className="text-lg font-bold min-h-[44px]"
                          />
                          {suggestion && (
                            <Button
                              variant="outline"
                              onClick={() => setMobilePrices({...mobilePrices, [editKey]: suggestion.suggested})}
                              className="border-purple-300 text-purple-600 min-h-[44px] whitespace-nowrap"
                            >
                              Usa €{suggestion.suggested}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Sticky bottom action bar - IMPROVED */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
          {/* Row 1: Main actions - 4 buttons compact */}
          <div className="p-1.5 grid grid-cols-4 gap-1">
            <Button
              variant="outline"
              onClick={() => setSyncDialogOpen(true)}
              disabled={syncLoading}
              className={`min-h-[40px] text-[10px] px-1 ${syncBreakdown.error > 0 ? 'border-red-500 text-red-600' : 'border-green-500 text-green-600'}`}
              data-testid="mobile-sync-btn"
            >
              <Upload className={`w-3.5 h-3.5 ${syncLoading ? 'animate-spin' : ''}`} />
              <span className="ml-0.5">Sync</span>
              {pendingSyncCount > 0 && (
                <span className={`ml-0.5 ${syncBreakdown.error > 0 ? 'bg-red-500' : 'bg-yellow-500'} text-white text-[9px] px-1 rounded-full`}>
                  {pendingSyncCount}
                </span>
              )}
            </Button>
            
            <Button
              onClick={getAISuggestions}
              disabled={aiLoading}
              className="min-h-[40px] text-[10px] px-1 bg-purple-600 hover:bg-purple-700"
              data-testid="mobile-ai-btn"
            >
              <Sparkles className={`w-3.5 h-3.5 ${aiLoading ? 'animate-pulse' : ''}`} />
              <span className="ml-0.5">AI</span>
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setQuickApplyDialogOpen(true)}
              className="min-h-[40px] text-[10px] px-1 border-blue-500 text-blue-600"
              data-testid="mobile-quick-apply-btn"
            >
              <Calendar className="w-3.5 h-3.5" />
              <span className="ml-0.5">Applica</span>
            </Button>
            
            <Button
              variant="outline"
              onClick={() => { loadCompetitorData(); setCompetitorPanelOpen(true); }}
              disabled={loadingCompetitors}
              className="min-h-[40px] text-[10px] px-1 border-amber-500 text-amber-600"
              data-testid="mobile-competitor-btn"
            >
              <TrendingUp className={`w-3.5 h-3.5 ${loadingCompetitors ? 'animate-pulse' : ''}`} />
              <span className="ml-0.5">Mercato</span>
            </Button>
          </div>
          
          {/* Row 2: Conditional actions */}
          <div className="px-1.5 pb-1.5 flex gap-1">
            {syncBreakdown.error > 0 && (
              <Button
                variant="outline"
                onClick={loadSyncErrors}
                className="flex-1 min-h-[36px] text-[10px] border-red-400 text-red-600"
                data-testid="mobile-errors-btn"
              >
                <AlertCircle className="w-3.5 h-3.5 mr-0.5" />
                Errori ({syncBreakdown.error})
              </Button>
            )}
            
            {Object.keys(aiSuggestions).length > 0 && !mobileEditMode && (
              <Button
                onClick={() => setApplyAllAiDialogOpen(true)}
                className="flex-1 min-h-[36px] text-[10px] bg-green-600 hover:bg-green-700"
                data-testid="mobile-apply-all-ai-btn"
              >
                <Check className="w-3.5 h-3.5 mr-0.5" />
                Applica AI ({Object.keys(aiSuggestions).length})
              </Button>
            )}
            
            {mobileEditMode && Object.keys(mobilePrices).length > 0 && (
              <Button
                onClick={saveMobilePrices}
                disabled={syncLoading}
                className="flex-1 min-h-[36px] text-[10px] bg-[#C5A059] hover:bg-[#B08D45]"
                data-testid="mobile-save-btn"
              >
                <Save className={`w-3.5 h-3.5 mr-0.5 ${syncLoading ? 'animate-spin' : ''}`} />
                Salva ({Object.keys(mobilePrices).length})
              </Button>
            )}
            
            {/* Refresh button */}
            <Button
              variant="ghost"
              onClick={loadData}
              disabled={loading}
              className="min-h-[36px] min-w-[36px] text-gray-500"
              data-testid="mobile-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ============== DESKTOP VIEW ==============
  const renderDesktopView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="font-cinzel text-2xl text-[#2D3436]">Calendario Prezzi AI</h1>
          <p className="text-gray-500 text-sm">Visualizza e modifica i prezzi per ogni giorno</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button 
            onClick={getAISuggestions}
            disabled={aiLoading}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="ai-suggest-btn"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${aiLoading ? 'animate-pulse' : ''}`} />
            {aiLoading ? 'Analisi AI...' : 'Suggerisci Prezzi AI'}
          </Button>
          <Button 
            onClick={() => setSyncDialogOpen(true)}
            disabled={syncLoading}
            variant="outline"
            className="border-green-500 text-green-600 hover:bg-green-50"
            data-testid="sync-beds24-btn"
          >
            <Upload className={`w-4 h-4 mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
            Sync Beds24
            {pendingSyncCount > 0 && (
              <span className="ml-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                {pendingSyncCount}
              </span>
            )}
          </Button>
          <Button 
            onClick={() => {
              setQuickApplyDialogOpen(true);
              const year = currentMonth.getFullYear();
              const month = currentMonth.getMonth();
              setQuickDateFrom(`${year}-${String(month + 1).padStart(2, '0')}-01`);
              setQuickDateTo(`${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`);
            }}
            variant="outline"
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
            data-testid="quick-apply-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Applica Rapido
          </Button>
          <Button 
            onClick={async () => {
              await loadSyncErrors();
              setSyncErrorsDialogOpen(true);
            }}
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50"
            data-testid="sync-errors-btn"
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Errori Sync
          </Button>
          <Button 
            onClick={async () => {
              await loadCompetitorData();
              setCompetitorPanelOpen(true);
            }}
            disabled={loadingCompetitors}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="competitor-btn"
          >
            {loadingCompetitors ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TrendingUp className="w-4 h-4 mr-2" />
            )}
            Analisi Mercato
          </Button>
        </div>
      </div>

      {/* Price Alert Banner */}
      {priceAlert && (
        <div className="mb-4 bg-yellow-50 border border-yellow-300 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-800">{priceAlert.title}</p>
                <p className="text-sm text-yellow-700">{priceAlert.message}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-yellow-500 text-yellow-700"
                onClick={() => setCompetitorPanelOpen(true)}
              >
                Vedi Dettagli
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPriceAlert(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestions Banner */}
      {Object.keys(aiSuggestions).length > 0 && (
        <Card className="border-purple-300 bg-gradient-to-r from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-800">
                    {Object.keys(aiSuggestions).length} Suggerimenti AI Pronti
                  </h3>
                  <p className="text-sm text-purple-600">
                    I prezzi suggeriti sono mostrati come badge viola sulle celle. 
                    Clicca &quot;Applica Tutti&quot; per salvarli nel calendario e sincronizzarli su Beds24.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setAiSuggestions({})}
                  className="border-purple-300 text-purple-600"
                >
                  <X className="w-4 h-4 mr-1" />
                  Annulla
                </Button>
                <Button
                  onClick={() => setApplyAllAiDialogOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Applica Tutti i Suggerimenti
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month Navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <Button variant="ghost" onClick={prevMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <CardTitle className="text-xl">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </CardTitle>
            <Button variant="ghost" onClick={nextMonth}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="overflow-x-auto">
          {/* Legend */}
          <div className="flex gap-3 mb-3 text-xs flex-wrap items-center">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-200 rounded"></div>
              <span>Prenotato</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-purple-200 rounded"></div>
              <span>Suggerimento AI</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-200 rounded"></div>
              <span>Selezionato</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-green-600" />
              <span>Sync OK</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <span>Da sincronizzare</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <span>Errore sync</span>
            </div>
            
            {Object.keys(aiSuggestions).length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="font-medium">{Object.keys(aiSuggestions).length} suggerimenti AI</span>
                </div>
                <Button
                  size="sm"
                  onClick={() => setApplyAllAiDialogOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="apply-all-ai-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Applica Tutti
                </Button>
              </div>
            )}
          </div>

          {/* Calendar Grid */}
          <div className="min-w-[800px]">
            {/* Days header */}
            <div className="grid gap-px mb-1" style={{ gridTemplateColumns: `120px repeat(${days.length}, minmax(42px, 1fr))` }}>
              <div className="font-semibold text-gray-600 text-xs p-1">Unità</div>
              {days.map(day => (
                <div 
                  key={day.dateStr}
                  className={`text-center text-[10px] p-0.5 rounded ${day.isToday ? 'bg-[#C5A059] text-white' : day.isWeekend ? 'bg-orange-50' : 'bg-gray-50'}`}
                >
                  <div className="font-semibold">{day.dayName}</div>
                  <div>{day.day}</div>
                </div>
              ))}
            </div>
            
            {/* Unit rows */}
            {units.map(unit => (
              <div 
                key={unit.id}
                className="grid gap-px mb-1"
                style={{ gridTemplateColumns: `120px repeat(${days.length}, minmax(42px, 1fr))` }}
              >
                <div className="font-semibold text-xs p-1 bg-gray-100 rounded flex items-center truncate" title={unit.nome}>
                  {unit.nome}
                </div>
                
                {days.map(day => {
                  const data = priceData[unit.id]?.[day.dateStr] || {};
                  const suggestion = aiSuggestions[day.dateStr];
                  const isSelected = selectedDates.includes(day.dateStr);
                  const comparison = getPriceComparison(data.price, suggestion?.suggested);
                  
                  let bgColor = 'bg-white hover:bg-gray-50';
                  if (data.booking) {
                    bgColor = 'bg-green-100';
                  } else if (isSelected) {
                    bgColor = 'bg-blue-200';
                  } else if (suggestion && Math.abs(suggestion.suggested - data.price) > 10) {
                    bgColor = 'bg-purple-50';
                  }
                  
                  return (
                    <div
                      key={`${unit.id}-${day.dateStr}`}
                      className={`relative p-1 rounded border cursor-pointer transition-all ${bgColor} ${isSelected ? 'ring-2 ring-blue-500' : ''} hover:shadow-md`}
                      onMouseDown={() => !data.booking && !editingCell && handleDateMouseDown(day.dateStr)}
                      onMouseEnter={() => handleDateMouseEnter(day.dateStr)}
                      onMouseUp={handleDateMouseUp}
                      onDoubleClick={() => handleCellDoubleClick(unit.id, day.dateStr, data.price, !!data.booking)}
                      title={data.booking ? `${data.booking.nome_ospite}` : suggestion ? `AI suggerisce: €${suggestion.suggested}` : 'Doppio click per modificare'}
                    >
                      {editingCell?.unitId === unit.id && editingCell?.dateStr === day.dateStr ? (
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={saveInlinePrice}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveInlinePrice();
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                          className="w-full text-center font-bold text-sm border-2 border-blue-500 rounded p-0.5 focus:outline-none"
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <>
                          <div className="text-center relative">
                            <span className={`font-bold text-xs ${data.booking ? 'text-green-700' : 'text-gray-800'}`}>
                              €{data.price || '-'}
                            </span>
                            {/* Sync status indicator */}
                            {!data.booking && data.syncStatus && (
                              <div className="absolute -bottom-0.5 right-0">
                                {data.syncStatus === 'verified' && (
                                  <div className="w-2 h-2 bg-green-500 rounded-full" title="Sincronizzato ✓"></div>
                                )}
                                {data.syncStatus === 'pending' && (
                                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" title="Da sincronizzare"></div>
                                )}
                                {data.syncStatus === 'error' && (
                                  <div className="w-2 h-2 bg-red-500 rounded-full" title="Errore sync"></div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {suggestion && !data.booking && (
                            <div className="absolute -top-1 -right-1">
                              <div className={`text-[9px] px-0.5 rounded-full ${suggestion.suggested > data.price ? 'bg-green-500' : suggestion.suggested < data.price ? 'bg-orange-500' : 'bg-purple-500'} text-white font-bold`}>
                                €{suggestion.suggested}
                              </div>
                            </div>
                          )}
                          
                          {comparison && !data.booking && (
                            <div className={`text-[8px] text-center ${comparison.color}`}>
                              {comparison.label}
                            </div>
                          )}
                          
                          {data.booking && (
                            <div className="text-[7px] text-green-700 truncate text-center leading-tight">
                              {data.booking.nome_ospite?.split(' ')[0] || 'Occ.'}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selection info */}
      {selectedDates.length > 0 && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white shadow-xl rounded-lg p-4 border-2 border-[#C5A059] z-50">
          <p className="text-sm">
            <strong>{selectedDates.length}</strong> giorni selezionati: {selectedDates[0]} → {selectedDates[selectedDates.length - 1]}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout title="Calendario Prezzi AI">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#C5A059]" />
        </div>
      ) : isMobile ? renderMobileView() : renderDesktopView()}

      {/* Apply Price Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Applica Prezzo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Periodo selezionato:</p>
              <p className="font-bold">
                {selectedDates.length > 0 && `${selectedDates.sort()[0]} → ${selectedDates.sort()[selectedDates.length - 1]}`}
              </p>
              <p className="text-xs text-gray-500">{selectedDates.length} giorni</p>
            </div>
            
            {selectedDates[0] && aiSuggestions[selectedDates[0]] && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="font-semibold text-purple-800">Suggerimento AI</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">€{aiSuggestions[selectedDates[0]].suggested}/notte</p>
                <p className="text-xs text-purple-600 mt-1">{aiSuggestions[selectedDates[0]].reason}</p>
                <Button 
                  size="sm" 
                  className="mt-2 bg-purple-600 hover:bg-purple-700"
                  onClick={() => setNewPrice(aiSuggestions[selectedDates[0]].suggested.toString())}
                >
                  Usa questo prezzo
                </Button>
              </div>
            )}
            
            <div>
              <Label>Prezzo per notte (€)</Label>
              <Input
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="150"
                className="text-xl font-bold"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Applica a:</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {units.map(unit => (
                  <label key={unit.id} className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={applyToUnits[unit.id] || false}
                      onChange={(e) => setApplyToUnits({...applyToUnits, [unit.id]: e.target.checked})}
                      className="w-5 h-5 text-[#C5A059] rounded"
                    />
                    <span>{unit.nome}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setApplyDialogOpen(false); setSelectedDates([]); }}>
              Annulla
            </Button>
            <Button onClick={applyPrice} className="bg-[#C5A059] hover:bg-[#B08D45]">
              <Check className="w-4 h-4 mr-2" />
              Applica Prezzo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync to Beds24 Dialog */}
      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Sincronizza Prezzi su Beds24
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!syncStatus?.errors?.length && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  Questa operazione sincronizzerà i <strong>prezzi modificati</strong> ({pendingSyncCount} in totale) su Beds24.
                </p>
                {pendingSyncCount > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {syncBreakdown.pending > 0 && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                        ⏳ {syncBreakdown.pending} da sincronizzare
                      </span>
                    )}
                    {syncBreakdown.error > 0 && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded">
                        ❌ {syncBreakdown.error} errori da ritentare
                      </span>
                    )}
                    {syncBreakdown.local_only > 0 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded">
                        💾 {syncBreakdown.local_only} solo locali
                      </span>
                    )}
                  </div>
                )}
                {pendingSyncCount === 0 && (
                  <p className="mt-2 text-xs text-green-600">✓ Tutti i prezzi sono sincronizzati</p>
                )}
              </div>
            )}
            
            {syncStatus && (
              <div className={`p-3 rounded-lg border ${
                syncStatus.status === 'verified' || syncStatus.status === 'all_verified' ? 'bg-green-50 border-green-200' :
                syncStatus.status === 'syncing' ? 'bg-blue-50 border-blue-200' :
                syncStatus.status === 'has_errors' ? 'bg-red-50 border-red-200' :
                'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-2">
                  {(syncStatus.status === 'verified' || syncStatus.status === 'all_verified') && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                  {syncStatus.status === 'syncing' && <Clock className="w-5 h-5 text-blue-600 animate-spin" />}
                  {syncStatus.status === 'has_errors' && <AlertCircle className="w-5 h-5 text-red-600" />}
                  {syncStatus.status === 'partial_verified' && <CheckCircle2 className="w-5 h-5 text-amber-600" />}
                  <span className={`text-sm font-medium ${
                    syncStatus.status === 'verified' || syncStatus.status === 'all_verified' ? 'text-green-800' :
                    syncStatus.status === 'syncing' ? 'text-blue-800' :
                    syncStatus.status === 'has_errors' ? 'text-red-800' :
                    'text-amber-800'
                  }`}>
                    {syncStatus.message}
                  </span>
                </div>
                
                {/* Summary counters */}
                {(syncStatus.success_count > 0 || syncStatus.error_count > 0) && (
                  <div className="flex gap-4 mt-2 text-sm">
                    {syncStatus.success_count > 0 && (
                      <span className="text-green-700">✓ {syncStatus.success_count} successi</span>
                    )}
                    {syncStatus.verified_count > 0 && (
                      <span className="text-green-600">({syncStatus.verified_count} verificati)</span>
                    )}
                    {syncStatus.error_count > 0 && (
                      <span className="text-red-700">✗ {syncStatus.error_count} errori</span>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* ERROR DETAILS */}
            {syncStatus?.errors?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Dettagli Errori ({syncStatus.errors.length})
                </h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {syncStatus.errors.map((err, idx) => (
                    <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-red-900">{err.unit_name}</p>
                          <p className="text-red-700">
                            Data: {err.date} {err.date_to && err.date_to !== err.date && `→ ${err.date_to}`}
                          </p>
                          <p className="text-red-700">Prezzo: €{err.price}</p>
                        </div>
                        <div className="text-right">
                          {err.http_status && (
                            <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                              HTTP {err.http_status}
                            </span>
                          )}
                          {err.retryable && (
                            <span className="inline-block ml-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded">
                              Ritentabile
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 p-2 bg-red-100 rounded text-red-900">
                        <p className="font-medium text-xs">Risposta Beds24:</p>
                        <p className="text-xs">{err.error || 'Errore sconosciuto'}</p>
                        {err.beds24_error_code && (
                          <p className="text-xs text-red-700">Codice: {err.beds24_error_code}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* SUCCESS DETAILS (collapsible) */}
            {syncStatus?.successful?.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-green-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Sincronizzazioni riuscite ({syncStatus.successful.length})
                </summary>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1 pl-6">
                  {syncStatus.successful.slice(0, 20).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-green-700 py-1 border-b border-green-100">
                      <span>{item.unit_name} - {item.date}</span>
                      <span>€{item.price} {item.status === 'verified' ? '✓' : ''}</span>
                    </div>
                  ))}
                  {syncStatus.successful.length > 20 && (
                    <p className="text-xs text-gray-500">... e altri {syncStatus.successful.length - 20}</p>
                  )}
                </div>
              </details>
            )}
            
            {!syncStatus?.errors?.length && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Verranno sincronizzati:</p>
                <ul className="text-sm space-y-1">
                  {units.map(unit => (
                    <li key={unit.id} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{unit.nome}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {!syncStatus?.errors?.length && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  ⚠️ I prezzi saranno inviati a Beds24 e <strong>verificati</strong> automaticamente. Solo i prezzi confermati saranno marcati come sincronizzati.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-wrap gap-2">
            {syncStatus?.errors?.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    // Download report
                    const report = JSON.stringify({
                      timestamp: new Date().toISOString(),
                      successful: syncStatus.successful || [],
                      errors: syncStatus.errors || []
                    }, null, 2);
                    const blob = new Blob([report], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `sync_report_${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                  }}
                  className="text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica Report
                </Button>
                <Button
                  onClick={async () => {
                    setSyncLoading(true);
                    try {
                      const retryResponse = await axios.post(
                        `${API}/admin/pricing/retry-failed`,
                        syncStatus.errors,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      if (retryResponse.data.retry_failed === 0) {
                        setSyncStatus({
                          status: 'all_verified',
                          message: `✓ Retry completato: ${retryResponse.data.retry_success} prezzi sincronizzati`,
                          success_count: retryResponse.data.retry_success,
                          error_count: 0,
                          successful: retryResponse.data.results,
                          errors: []
                        });
                        toast.success('Retry completato con successo!');
                      } else {
                        const newErrors = retryResponse.data.results.filter(r => r.status === 'error');
                        const newSuccess = retryResponse.data.results.filter(r => r.status !== 'error');
                        setSyncStatus({
                          status: 'has_errors',
                          message: retryResponse.data.message,
                          success_count: retryResponse.data.retry_success,
                          error_count: retryResponse.data.retry_failed,
                          successful: [...(syncStatus.successful || []), ...newSuccess],
                          errors: newErrors
                        });
                        toast.warning(`Retry: ${retryResponse.data.retry_success} successi, ${retryResponse.data.retry_failed} ancora falliti`);
                      }
                    } catch (error) {
                      toast.error('Errore durante il retry');
                    } finally {
                      setSyncLoading(false);
                    }
                  }}
                  disabled={syncLoading}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
                  Riprova Solo Errori ({syncStatus.errors.length})
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setSyncDialogOpen(false)}>
              {syncStatus?.errors?.length ? 'Chiudi' : 'Annulla'}
            </Button>
            {!syncStatus?.errors?.length && (
              <Button 
                onClick={syncAllToBeds24} 
                disabled={syncLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {syncLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sincronizzazione...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Sincronizza e Verifica
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Apply Price Dialog */}
      <Dialog open={quickApplyDialogOpen} onOpenChange={setQuickApplyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              Applica Prezzo Rapido
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                Inserisci le date e il prezzo da applicare. Il prezzo verrà impostato per <strong>tutti i giorni</strong> nel range selezionato.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Inizio</Label>
                <Input
                  type="date"
                  value={quickDateFrom}
                  onChange={(e) => setQuickDateFrom(e.target.value)}
                  className="min-h-[44px]"
                />
              </div>
              <div>
                <Label>Data Fine</Label>
                <Input
                  type="date"
                  value={quickDateTo}
                  onChange={(e) => setQuickDateTo(e.target.value)}
                  className="min-h-[44px]"
                />
              </div>
            </div>
            
            <div>
              <Label>Prezzo per notte (€)</Label>
              <Input
                type="number"
                value={quickPrice}
                onChange={(e) => setQuickPrice(e.target.value)}
                placeholder="150"
                className="text-xl font-bold min-h-[44px]"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Applica a:</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {units.map(unit => (
                  <label key={unit.id} className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={applyToUnits[unit.id] || false}
                      onChange={(e) => setApplyToUnits({...applyToUnits, [unit.id]: e.target.checked})}
                      className="w-5 h-5 text-orange-500 rounded"
                    />
                    <span>{unit.nome}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickApplyDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={quickApplyPrice} className="bg-orange-500 hover:bg-orange-600">
              <Check className="w-4 h-4 mr-2" />
              Applica Prezzo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply All AI Suggestions Dialog */}
      <Dialog open={applyAllAiDialogOpen} onOpenChange={setApplyAllAiDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Applica Tutti i Suggerimenti AI
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                Stai per applicare <strong>{Object.keys(aiSuggestions).length} suggerimenti AI</strong> al calendario prezzi.
              </p>
              <p className="text-xs text-purple-600 mt-2">
                I prezzi verranno salvati nel database E sincronizzati su Beds24 con verifica.
              </p>
            </div>
            
            {/* Preview of prices */}
            <div className="p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
              <p className="text-xs text-gray-600 mb-2">Anteprima suggerimenti:</p>
              <div className="space-y-1 text-sm">
                {Object.entries(
                  Object.entries(aiSuggestions).reduce((acc, [date, data]) => {
                    const price = data.suggested;
                    if (!acc[price]) acc[price] = [];
                    acc[price].push(date);
                    return acc;
                  }, {})
                ).slice(0, 5).map(([price, dates]) => (
                  <div key={price} className="flex justify-between">
                    <span className="text-gray-600">{dates.length} giorni</span>
                    <span className="font-bold text-purple-700">€{price}/notte</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Applica a:</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {units.map(unit => (
                  <label key={unit.id} className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={applyToUnits[unit.id] || false}
                      onChange={(e) => setApplyToUnits({...applyToUnits, [unit.id]: e.target.checked})}
                      className="w-5 h-5 text-purple-500 rounded"
                    />
                    <span>{unit.nome}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800">
                ⚠️ Questa operazione modificherà i prezzi nel database e su Beds24. I prezzi uguali a quelli attuali verranno saltati.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyAllAiDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={applyAllAiSuggestions} 
              disabled={applyingAllAi}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {applyingAllAi ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Applicazione...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Applica {Object.keys(aiSuggestions).length} Suggerimenti
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Errori Sincronizzazione */}
      <Dialog open={syncErrorsDialogOpen} onOpenChange={setSyncErrorsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Errori Sincronizzazione Beds24
            </DialogTitle>
            <DialogDescription>
              Prezzi che non sono stati sincronizzati correttamente con Beds24
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {syncErrors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium">Nessun errore!</p>
                <p className="text-sm">Tutti i prezzi sono sincronizzati correttamente.</p>
              </div>
            ) : (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">
                    <strong>{syncErrors.length} prezzi</strong> non sincronizzati. 
                    Clicca &quot;Riprova Tutti&quot; per tentare nuovamente.
                  </p>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-2">Unità</th>
                        <th className="text-left p-2">Data</th>
                        <th className="text-right p-2">Prezzo</th>
                        <th className="text-left p-2">Errore</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncErrors.slice(0, 20).map((error, idx) => (
                        <tr key={idx} className="border-t hover:bg-gray-50">
                          <td className="p-2 font-medium">{error.unit_name || 'Unknown'}</td>
                          <td className="p-2">{error.date}</td>
                          <td className="p-2 text-right">€{error.price}</td>
                          <td className="p-2 text-red-600 text-xs">{error.error?.substring(0, 50) || 'Errore sconosciuto'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {syncErrors.length > 20 && (
                    <div className="p-2 bg-gray-50 text-center text-sm text-gray-500">
                      ... e altri {syncErrors.length - 20} errori
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setSyncErrorsDialogOpen(false)}>
              Chiudi
            </Button>
            {syncErrors.length > 0 && (
              <Button 
                onClick={async () => {
                  const result = await retryAllFailed();
                  if (result) {
                    await loadSyncErrors();
                  }
                }}
                disabled={retryingSync}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {retryingSync ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Riprovo...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Riprova Tutti ({syncErrors.length})
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Competitor Analysis Panel */}
      <Dialog open={competitorPanelOpen} onOpenChange={setCompetitorPanelOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <TrendingUp className="w-5 h-5" />
              Analisi Prezzi Mercato Paestum
            </DialogTitle>
            <DialogDescription>
              Confronto con {competitorData?.market_data?.competitors_found || 0} competitor reali da Booking.com
            </DialogDescription>
          </DialogHeader>
          
          {competitorData ? (
            <div className="space-y-6">
              {/* Market Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-blue-600 uppercase">Media Mercato</p>
                  <p className="text-2xl font-bold text-blue-700">€{competitorData.market_data?.avg_price}</p>
                  <p className="text-xs text-blue-500">/notte</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-green-600 uppercase">Prezzo Minimo</p>
                  <p className="text-2xl font-bold text-green-700">€{competitorData.market_data?.min_price}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-red-600 uppercase">Prezzo Massimo</p>
                  <p className="text-2xl font-bold text-red-700">€{competitorData.market_data?.max_price}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-purple-600 uppercase">Competitor</p>
                  <p className="text-2xl font-bold text-purple-700">{competitorData.market_data?.competitors_found}</p>
                  <p className="text-xs text-purple-500">strutture</p>
                </div>
              </div>

              {/* Your Prices vs Market */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">
                  I Tuoi Prezzi vs Mercato
                </div>
                <div className="divide-y">
                  {competitorData.suggestions?.map((s, idx) => (
                    <div key={idx} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{s.unit_name}</p>
                        <p className="text-sm text-gray-500">Prezzo attuale: €{s.current_price}/notte</p>
                      </div>
                      <div className="text-right">
                        {s.comparison === 'sotto_media' && (
                          <div className="flex items-center gap-2 text-yellow-600">
                            <ArrowUp className="w-4 h-4" />
                            <span className="font-bold">Suggerito: €{s.suggested_price}</span>
                          </div>
                        )}
                        {s.comparison === 'sopra_media' && (
                          <div className="flex items-center gap-2 text-orange-600">
                            <ArrowDown className="w-4 h-4" />
                            <span className="font-bold">Suggerito: €{s.suggested_price}</span>
                          </div>
                        )}
                        {s.comparison === 'in_linea' && (
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="font-bold">In linea ✓</span>
                          </div>
                        )}
                        <p className="text-xs text-gray-400">{s.reasoning?.substring(0, 60)}...</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Competitors */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">
                  Top 10 Competitor a Paestum
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Nome</th>
                        <th className="text-center p-2">Rating</th>
                        <th className="text-right p-2">Prezzo/notte</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {competitorData.competitors?.slice(0, 10).map((c, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-2">
                            <p className="font-medium truncate max-w-[200px]">{c.name}</p>
                            <p className="text-xs text-gray-400">{c.source === 'booking_scrape' ? '🔄 Booking.com' : '📁 Database'}</p>
                          </td>
                          <td className="p-2 text-center">
                            {c.rating && (
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">
                                {c.rating}
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-right font-bold text-gray-700">
                            €{c.price_per_night || Math.round(c.price / 2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Dati aggiornati: {competitorData.generated_at?.substring(0, 19).replace('T', ' ')} | 
                Periodo: {competitorData.period}
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-blue-500" />
              <p>Caricamento dati competitor...</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompetitorPanelOpen(false)}>
              Chiudi
            </Button>
            <Button 
              onClick={loadCompetitorData}
              disabled={loadingCompetitors}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loadingCompetitors ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Aggiornando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Aggiorna Dati
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
